import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../Reveal";
import { PROFILE } from "../../lib/content";
import { createContact } from "../../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await createContact(form);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-surface/40 border-t border-gold/10" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* left */}
        <Reveal>
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-gold">Get in Touch</span>
            <h2 className="font-serif text-4xl md:text-6xl font-light mt-4 leading-tight text-foreground">
              Let's build something <span className="text-gradient-gold italic">that matters.</span>
            </h2>
            <p className="mt-6 text-muted-foreground md:text-lg font-light max-w-md">
              For collaborations, speaking, mentorship or media — reach out directly, or book a paid consultation for focused guidance.
            </p>

            <div className="mt-10 space-y-5">
              <a href={`mailto:${PROFILE.email}`} className="flex items-center gap-4 text-foreground hover:text-gold transition-colors" data-testid="contact-email-link">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><Mail size={18} /></span>
                {PROFILE.email}
              </a>
              <a href={`tel:${PROFILE.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 text-foreground hover:text-gold transition-colors">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><Phone size={18} /></span>
                {PROFILE.phone}
              </a>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><MapPin size={18} /></span>
                {PROFILE.location}
              </div>
              <a href={PROFILE.socials.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-4 text-foreground hover:text-gold transition-colors">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold"><Linkedin size={18} /></span>
                LinkedIn
              </a>
            </div>

            <Link
              to="/book"
              data-testid="contact-book-btn"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors"
            >
              Book a Consultation <ArrowUpRight size={18} />
            </Link>
          </div>
        </Reveal>

        {/* form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="rounded-2xl border border-gold/15 bg-card p-8 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</label>
                <input value={form.name} onChange={update("name")} data-testid="contact-name"
                  className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none transition-colors" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</label>
                <input type="email" value={form.email} onChange={update("email")} data-testid="contact-email"
                  className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none transition-colors" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Subject</label>
              <input value={form.subject} onChange={update("subject")} data-testid="contact-subject"
                className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none transition-colors" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Message</label>
              <textarea rows={5} value={form.message} onChange={update("message")} data-testid="contact-message"
                className="mt-2 w-full bg-input/40 border border-gold/15 rounded-lg px-4 py-3 text-foreground focus:border-gold outline-none transition-colors resize-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
